import { fromTable } from '@/integrations/supabase/db';
import { SearchResult, SearchSectionId } from '../types';
import { getMatchFunction, getServerPattern, getServerLimit, calculateRelevanceScore } from '../utils';

export async function searchAnsprechpartner(
  term: string, 
  limit: number, 
  exactMatch: boolean,
  fuzzyMatch: boolean = false,
  signal?: AbortSignal
): Promise<SearchResult[]> {
  if (!term.trim()) return [];
  
  const pattern = getServerPattern(term, fuzzyMatch);
  const serverLimit = getServerLimit(limit, fuzzyMatch);
  const matchFn = getMatchFunction(exactMatch, fuzzyMatch);
  
  // Step 1: Base query without embedded joins
  const { data, error } = await fromTable('ansprechpartner')
    .select('id, vorname, nachname, email, telefonnummer, gesellschaft_id, updated_at')
    .or(`vorname.ilike.${pattern},nachname.ilike.${pattern},email.ilike.${pattern}`)
    .limit(serverLimit);

  if (error) {
    console.error('Ansprechpartner search error:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // Step 2: Lookup gesellschaften
  const gesellschaftIds = [...new Set(data.map(a => a.gesellschaft_id).filter(Boolean))];
  
  const gesellschaftenRes = gesellschaftIds.length > 0 
    ? await fromTable('gesellschaften').select('id, name').in('id', gesellschaftIds)
    : { data: [] };

  const gesellschaftById = new Map((gesellschaftenRes.data || []).map((g: any) => [g.id, g.name]));

  // Step 3: Client-side multi-term filter and map
  return data
    .map(a => {
      const gesellschaftName = gesellschaftById.get(a.gesellschaft_id);
      return { ...a, gesellschaft_name: gesellschaftName };
    })
    .filter(a => matchFn(term, a.vorname, a.nachname, a.email, a.gesellschaft_name))
    .slice(0, limit)
    .map(a => {
      const title = `${a.vorname || ''} ${a.nachname || ''}`.trim() || 'Unbenannt';
      const subtitle = [a.email, a.gesellschaft_name].filter(Boolean).join(' • ');
      
      return {
        id: a.id,
        type: 'ansprechpartner' as SearchSectionId,
        title,
        subtitle,
        badge: 'Ansprechpartner',
        data: a,
        relevanceScore: calculateRelevanceScore(term, title, subtitle, a.updated_at)
      };
    });
}
